<template lang="pug">
.vprContentsContentComponent
  .title {{ userItem.vpr.title }}

  .main
    .leftSide
      ui-user-item.item(
        :user-item="userItem"
        :showInfo="true"
        :isClickable="false"
        :showFavorite="isAuthenticated"
        @action:click-on-info="onInfoClick"
      )
      .footer
        ui-button.button(
          v-if="!userItemIsBought"
          @click="onPurchaseButtonClick"
        ) {{ userItem.purchaseText }}
        template(v-if="isAuthenticated && subscription?.isDemo && !userItemIsBought")
          .text Демо-доступ до {{ subscription.expires }}
        template(v-else-if="userItemIsBought")
          .text Подписка активна до {{ userItemExpires }}

    .rightSide
      .header
        .title Стартовая диагностика
        .buttons
          .buttonWrap(v-if="!isVprCompleted")
            ui-button(
              size="s"
              :is-disabled="isReadonly"
              @click="onGoToVprClick"
            ) Пройти
            .hint(v-if="isReadonly") Нет доступа к ВПР. Активируйте демо&#8209;доступ или приобретите ВПР.
          template(v-else)
            ui-button(
              color="blue-outline"
              size="s"
              @click="onResetVprClick"
              :is-loading="isVprResetLoading"
              :is-disabled="isReadonly"
            ) Пройти ещё раз
            ui-button(
              color="blue-outline"
              size="s"
              @click="onResultVprClick"
              :is-disabled="isVprResetLoading || isReadonly"
            ) Результат
            ui-button(
              size="s"
              @click="onHowtoVprClick"
              :is-disabled="isVprResetLoading || isReadonly"
            ) Разбор

      .title Тренажёры

      .themes
        vpr-theme-component.theme(
          v-for="theme in userItem.vpr.controlworks"
          :key="theme.id"
          :theme="theme"
          :is-readonly="isReadonly"
        )
</template>

<script>
import { computed, inject, onUnmounted } from 'vue'
import router from '@/router'
import UserItem from '@/models/UserItem'
import VprThemeComponent from './VprContentsTheme'
import VprContentsModal from './VprContentsModal'
import moment from 'moment'

export default {
  components: {
    VprThemeComponent
  },
  props: {
    userItem: {
      type: UserItem,
      required: true
    }
  },
  setup (props) {
    const store = inject('store')
    const isAuthenticated = computed(() => store.user.isAuthenticated)
    const toVpr = () => router.push({ name: 'VprStartWork' })
    const toProgress = () => router.push({ name: 'VprProgress' })
    const toHowto = () => router.push({ name: 'VprHowto' })

    const controller = inject('controller')

    controller.$on('requestChildVprReset-success', toVpr)
    onUnmounted(() => controller.$off('requestChildVprReset-success', toVpr))

    controller.$on('requestChildVprReset-error', toProgress)
    onUnmounted(() => controller.$off('requestChildVprReset-error', toProgress))

    const subscription = computed(() => store.user.profile?.subscription)
    const subscriptions = computed(() => store.user.profile?.subscriptions)

    const subscriptionsItem = computed(() =>
      subscriptions.value?.find(el => el.nomenklatura === props.userItem.nomenklatura)
    )
    const userItemIsBought = computed(() => subscriptionsItem.value?.bought)
    const userItemExpires = computed(() => {
      return subscriptionsItem.value?.expires
        ? moment(subscriptionsItem.value?.expires).format('DD.MM.YYYY')
        : null
    })
    const isReadonly = computed(
      () => (!isAuthenticated.value || subscription.value.isNone || subscription.value.isOver) && !userItemIsBought.value
    )
    const onInfoClick = () => {
      store.modalQueue.push({
        key: 'vprinfo',
        component: VprContentsModal,
        props: {
          vpr: props.userItem.vpr
        }
      })
    }
    const onGoToVprClick = () => {
      toVpr()
    }
    const onResetVprClick = () => {
      controller.resetVprStateRequest()
    }
    const onResultVprClick = () => {
      toVpr()
    }
    const onHowtoVprClick = () => {
      toHowto()
    }

    const onPurchaseButtonClick = () => {
      isAuthenticated.value ? props.userItem.goToPurchaseLink() : store.user.login()
    }

    return {
      controller,
      toVpr,
      toProgress,
      toHowto,
      isVprCompleted: computed(
        () => controller.childVpr.value.userItem.vpr.progress?.startworkProgress?.completed ?? false
      ),
      isVprResetLoading: computed(() => controller.isVprResetLoading?.value ?? false),
      subscription,
      userItemIsBought,
      userItemExpires,
      isReadonly,
      isAuthenticated,
      onInfoClick,
      onGoToVprClick,
      onResetVprClick,
      onResultVprClick,
      onHowtoVprClick,
      onPurchaseButtonClick
    }
  }
}
</script>

<style lang="sass" scoped>
.vprContentsContentComponent

  > .title
    @include font('h1')
    margin-bottom: 12.5*$u
    @media (max-width: $padWidth)
      display: none

  > .main
    display: flex
    align-items: flex-start
    @include gap2d(8*$u)

    > .leftSide
      flex: 0 0 102*$u
      @media (max-width: $XXXLWidth)
        flex: 0 0 84*$u
      @media (max-width: $padWidth)
        flex: 0 0 75*$u
      @media (max-width: $MWidth)
        flex: initial

      .footer
        .button
          ~ .text
            margin-top: 3*$u
        .text
          color: $cDarkGrey
          @include font('t16-regular')
          + .text
            margin-top: 3*$u

    > .rightSide
      flex: 1
      height: fit-content
      display: flex
      flex-flow: column
      border-radius: $brL
      background-color: $cWhite
      padding: 8*$u
      @include gap(5*$u, 'column')
      @media (max-width: $XLWidth)
        @include gap(4*$u, 'column')
        padding: 5*$u 4*$u

      > .header
        display: flex
        align-items: center
        justify-content: space-between
        border-radius: $brM
        background-color: $cElements
        padding: 4*$u 8*$u
        @include gap(3*$u)
        @media (max-width: $XLWidth)
          padding: 4*$u
          flex-direction: column
          align-items: initial
          @include gap(3*$u, 'column')

        > .title
          @include font('h2')
          @media (max-width: $XLWidth)
            @include font('m-h2')

        > .buttons
          display: flex
          align-items: center
          @include gap(4*$u)

          > .buttonWrap
            position: relative
            &:hover
              > .hint
                opacity: 1

            > .hint
              position: absolute
              transition: $animationSpeed
              pointer-events: none
              opacity: 0
              background: $cWhite
              padding: 3*$u 4*$u
              right: 0
              top: calc(100% + #{$u})
              width: 77*$u
              z-index: 1
              border-radius: $brS
              @include font('t14-regular')
              line-height: 1.25
              color: $cText
              box-shadow: $boxShadow
              @media (max-width: $XLWidth)
                right: initial
                left: 0
      > .title
        @include font('h1')
        @media (max-width: $XLWidth)
          @include font('m-h1')

      > .themes
        display: flex
        flex-flow: column
        @include gap(5*$u, "column")
        @media (max-width: $XLWidth)
          @include gap(4*$u, "column")

  @media (max-width: $MWidth)
    .main
      flex-direction: column
      align-items: initial
      .rightSide
        max-width: 100%
</style>
